/**
 * BuildSessionsTracker — per-app MCP build session activity (chronological).
 * Data from /cp/observability/sessions/:app_id and /cp/build-sessions.
 */
import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import {
  Wifi, Clock, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronRight, Copy, Loader2,
} from 'lucide-react';

const CP_BASE = import.meta.env.VITE_CP_BASE || '/cp';

function getHeaders() {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const s = useAuthStore.getState();
    if (s?.token) h['Authorization'] = `Bearer ${s.token}`;
    if (s?.selectedTenant) h['x-tenant-id'] = s.selectedTenant;
  } catch { /* ignore */ }
  return h;
}

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${CP_BASE}${path}`, { headers: getHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function StatusDot({ status }: { status: string }) {
  if (status === 'connected') return <span className="inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />;
  if (status === 'idle') return <span className="inline-block w-2 h-2 rounded-full bg-yellow-500" />;
  return <span className="inline-block w-2 h-2 rounded-full bg-gray-400" />;
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).catch(() => {});
}

interface BuildSessionsTrackerProps {
  appId: string;
  appName?: string;
}

export default function BuildSessionsTracker({ appId, appName }: BuildSessionsTrackerProps) {
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const qSessions = useQuery({
    queryKey: ['observability', 'sessions', appId],
    queryFn: () => fetchJson<any>(`/observability/sessions/${appId}`),
    refetchInterval: 5000,
    enabled: Boolean(appId),
  });

  const sessions = qSessions.data?.sessions || [];
  const activeSession = selectedSession || sessions[0]?.key_id || null;

  const qSteps = useQuery({
    queryKey: ['observability', 'session-steps', appId, activeSession],
    queryFn: () => fetchJson<any>(`/observability/sessions/${appId}/${encodeURIComponent(activeSession!)}/steps`),
    refetchInterval: 5000,
    enabled: Boolean(appId) && Boolean(activeSession),
  });

  const handleCopy = useCallback((text: string, label: string) => {
    copyToClipboard(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  }, []);

  if (sessions.length === 0 && !qSessions.isLoading) return null;

  const currentSession = sessions.find((s: any) => s.key_id === activeSession);
  const allEvents = (qSteps.data?.steps || []).flatMap((step: any) =>
    (step.events || []).map((ev: any) => ({ ...ev, step_title: step.step_title, step_id: step.step_id }))
  ).sort((a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime());

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Wifi size={16} className="text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-800">Build Sessions</h3>
        </div>
        {sessions.length > 1 && (
          <select
            className="text-xs border rounded px-2 py-1"
            value={activeSession || ''}
            onChange={(e) => setSelectedSession(e.target.value)}
          >
            {sessions.map((s: any) => (
              <option key={s.key_id} value={s.key_id}>
                {s.key_id} ({s.connection_status})
              </option>
            ))}
          </select>
        )}
      </div>

      {currentSession && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <StatusDot status={currentSession.connection_status} />
            <span className="capitalize font-medium">{currentSession.connection_status}</span>
            <span className="text-gray-400 text-xs">({currentSession.key_id})</span>
          </div>
          <div className="text-xs text-gray-400">
            {currentSession.total_calls} calls | Last: {new Date(currentSession.last_active).toLocaleTimeString()}
          </div>
        </div>
      )}

      <div className="max-h-[350px] overflow-y-auto p-3 space-y-1">
        {qSteps.isLoading && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 size={14} className="animate-spin" /> Loading session events…
          </div>
        )}
        {allEvents.length === 0 && !qSteps.isLoading && (
          <p className="text-xs text-gray-400 text-center py-4">No MCP activity recorded for this session yet.</p>
        )}
        {allEvents.map((ev: any, i: number) => {
          const key = `${ev.event_id || ev.tool_name}-${i}`;
          const isExpanded = expandedEvent === key;
          return (
            <div
              key={key}
              className={`p-2 rounded text-xs border cursor-pointer ${
                !ev.success ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50 hover:bg-gray-100'
              }`}
              onClick={() => setExpandedEvent(isExpanded ? null : key)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {ev.success ? <CheckCircle2 size={10} className="text-green-500" /> : <XCircle size={10} className="text-red-500" />}
                  <span className="text-gray-400">{new Date(ev.time).toLocaleTimeString()}</span>
                  <span className="font-mono text-gray-600">{ev.tool_name || ev.event_type}</span>
                  {ev.step_title && <span className="text-gray-400">· {ev.step_title}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">{(ev.duration_ms / 1000).toFixed(1)}s</span>
                  {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </div>
              </div>
              {isExpanded && (
                <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                  {ev.input_summary && <div className="text-gray-500 truncate">{ev.input_summary.slice(0, 200)}</div>}
                  {ev.error && (
                    <div className="text-red-600 flex items-center gap-1">
                      <AlertTriangle size={10} /> {ev.error}
                    </div>
                  )}
                  <button
                    type="button"
                    className="text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(JSON.stringify(ev, null, 2), key);
                    }}
                  >
                    <Copy size={10} /> Copy event
                    {copied === key && <span className="text-green-500 ml-1">Copied</span>}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {allEvents.some((ev: any) => !ev.success) && (
        <div className="px-4 py-2 border-t border-gray-100">
          <button
            type="button"
            className="text-[10px] text-blue-500 hover:text-blue-700 flex items-center gap-1"
            onClick={() => {
              const failed = allEvents.filter((ev: any) => !ev.success);
              handleCopy(JSON.stringify(failed, null, 2), 'errors');
            }}
          >
            <Copy size={10} /> Copy failed events
            {copied === 'errors' && <span className="text-green-500 ml-1">Copied</span>}
          </button>
        </div>
      )}
    </div>
  );
}
